/**
 * slashCommands.tsx — typed slash-command registry + parser (frontend half).
 *
 * Single source of truth for the Ask composer's slash commands. Mirrors the
 * backend contract (services/command_contract.py) so a command means the same
 * thing on both sides. Validated with Zod so malformed command payloads are
 * caught at runtime instead of silently producing the wrong answer mode.
 *
 * This is a .tsx (not .ts) because the registry carries JSX icon nodes.
 */
import type { ReactNode } from "react";
import { z } from "zod";
import {
  Check,
  HelpCircle,
  List,
  Wand2,
  Mic,
  Sparkles,
  Code2,
  Languages,
  Image,
} from "lucide-react";

export const SLASH_COMMAND_SCHEMA = z.object({
  cmd: z.string().regex(/^\/[a-z_]+$/),
  label: z.string(),
  icon: z.any().optional(),
  mode: z
    .enum([
      "normal",
      "exam",
      "manual",
      "stepwise",
      "socratic",
      "podcast",
      "minimal",
      "text_image",
      "genius",
    ])
    .optional(),
  prompt: z.string().optional(),
});
export type SlashCommand = z.infer<typeof SLASH_COMMAND_SCHEMA> & {
  icon?: ReactNode;
};

/** Canonical metadata per command, shared with the backend registry. */
export type CommandInteraction =
  | "answer"
  | "turn_based"
  | "progressive"
  | "artifact";

export const AI_COMMANDS: Record<
  string,
  { kind: "mode" | "prompt"; mode?: string; interaction: CommandInteraction }
> = {
  standard: { kind: "mode", mode: "normal", interaction: "answer" },
  exam: { kind: "mode", mode: "exam", interaction: "answer" },
  manual: { kind: "mode", mode: "manual", interaction: "answer" },
  socratic: { kind: "mode", mode: "socratic", interaction: "answer" },
  podcast: { kind: "mode", mode: "podcast", interaction: "artifact" },
  stepwise: { kind: "mode", mode: "stepwise", interaction: "progressive" },
  minimal: { kind: "mode", mode: "minimal", interaction: "answer" },
  text_image: { kind: "mode", mode: "text_image", interaction: "answer" },
  genius: { kind: "mode", mode: "genius", interaction: "answer" },
  summarize: { kind: "prompt", interaction: "answer" },
  explain: { kind: "prompt", interaction: "answer" },
  code: { kind: "prompt", interaction: "answer" },
  translate: { kind: "prompt", interaction: "answer" },
  recall: { kind: "prompt", interaction: "turn_based" },
  teach: { kind: "prompt", interaction: "turn_based" },
};

export const SLASH_COMMANDS: SlashCommand[] = [
  {
    cmd: "/standard",
    label: "Standard — default 5-section answer",
    mode: "normal",
    icon: <Check size={12} />,
  },
  {
    cmd: "/exam",
    label: "Brief answer + detailed explanation",
    mode: "exam",
    icon: <HelpCircle size={12} />,
  },
  {
    cmd: "/manual",
    label: "Literal step-by-step manual",
    mode: "manual",
    icon: <List size={12} />,
  },
  {
    cmd: "/socratic",
    label: "Research mode — web, images & video",
    mode: "socratic",
    icon: <Wand2 size={12} />,
  },
  {
    cmd: "/podcast",
    label: "Audio-only podcast answer",
    mode: "podcast",
    icon: <Mic size={12} />,
  },
  {
    cmd: "/stepwise",
    label: "Guided stepwise walkthrough",
    mode: "stepwise",
    icon: <List size={12} />,
  },
  {
    cmd: "/minimal",
    label: "Fast text-only answer",
    mode: "minimal",
    icon: <HelpCircle size={12} />,
  },
  {
    cmd: "/text_image",
    label: "Text + image answer",
    mode: "text_image",
    icon: <Image size={12} />,
  },
  {
    cmd: "/genius",
    label: "Genius — practice problems + exam questions",
    mode: "genius",
    icon: <Sparkles size={12} />,
  },
  {
    cmd: "/summarize",
    label: "Condense the thread so far",
    prompt: "Summarize the key points concisely:\n\n",
    icon: <Wand2 size={12} />,
  },
  {
    cmd: "/explain",
    label: "Slow it down, step by step",
    prompt: "Explain this step by step with examples:\n\n",
    icon: <HelpCircle size={12} />,
  },
  {
    cmd: "/code",
    label: "Answer with a runnable snippet",
    prompt: "Provide runnable code examples with explanation:\n\n",
    icon: <Code2 size={12} />,
  },
  {
    cmd: "/translate",
    label: "Convert to another language",
    prompt: "Translate and explain this academically:\n\n",
    icon: <Languages size={12} />,
  },
  {
    cmd: "/recall",
    label: "Quiz me — test my recall",
    prompt:
      "Quiz me to test my recall of this topic. Ask me one question at a time, wait for my answer, then tell me if I'm right and why. Keep going until I ask you to stop:\n\n",
    icon: <HelpCircle size={12} />,
  },
  {
    cmd: "/teach",
    label: "I'll explain it back to you",
    prompt:
      "I will explain this back to you. Listen to my explanation, then point out anything I got wrong, missed, or could improve. Be encouraging but precise:\n\n",
    icon: <HelpCircle size={12} />,
  },
];

/** Parse ``/cmd [args] : prompt`` into its parts (pure, no side effects). */
export interface ParsedSlashCommand {
  cmd: string;
  args?: string;
  prompt?: string;
}

const SLASH_PARSE_RE = /^\s*(\/[a-zA-Z_]+)(?:\s+(.+?))?(?:\s*:\s*(.*))?$/;

export function parseSlashCommand(text: string): ParsedSlashCommand | null {
  if (!text) return null;
  const m = SLASH_PARSE_RE.exec(text.trim());
  if (!m) return null;
  const cmd = m[1].toLowerCase();
  if (!SLASH_COMMANDS.some((c) => c.cmd === cmd)) return null;
  return {
    cmd,
    args: m[2]?.trim() || undefined,
    prompt: m[3]?.trim() || undefined,
  };
}

/** Validate a command object at runtime (Zod). Returns null when invalid. */
export function safeSlashCommand(value: unknown): SlashCommand | null {
  const res = SLASH_COMMAND_SCHEMA.safeParse(value);
  return res.success ? (res.data as SlashCommand) : null;
}
